import type { UniversityStudentsQuery } from "../students/types";

/** GraphQL `default_max_page_size` is 100; requesting more is silently clamped. */
export const EXPORT_PAGE_SIZE = 100;
export const EXPORT_MAX_PAGES = 50;

type PageInfo = {
  hasNextPage?: boolean | null;
  endCursor?: string | null;
};

type StudentsConnection = {
  nodes?: UniversityStudentsQuery["universityStudents"]["nodes"] | null;
  pageInfo?: PageInfo | null;
};

type FetchStudentsPage = (options: {
  variables: {
    first: number;
    after?: string;
    input: {
      universityLegacyIds: string[];
      alumni: boolean;
      search?: string;
    };
  };
}) => Promise<{ data?: { universityStudents?: StudentsConnection | null } | null; error?: Error }>;

export const fetchAllStudentsForExport = async ({
  fetchPage,
  universityLegacyId,
  search
}: {
  fetchPage: FetchStudentsPage;
  universityLegacyId: string;
  search?: string;
}): Promise<{ nodes: NonNullable<StudentsConnection["nodes"]>; truncated: boolean }> => {
  const allNodes: NonNullable<StudentsConnection["nodes"]> = [];
  let after: string | undefined;
  let hasNextPage = true;
  let pageCount = 0;
  const input = {
    universityLegacyIds: [universityLegacyId],
    alumni: false,
    search
  };

  while (hasNextPage && pageCount < EXPORT_MAX_PAGES) {
    const { data, error } = await fetchPage({
      variables: {
        first: EXPORT_PAGE_SIZE,
        after,
        input
      }
    });

    if (error) {
      throw error;
    }

    const connection = data?.universityStudents;
    const nodes = connection?.nodes || [];
    const endCursor = connection?.pageInfo?.endCursor;
    const nextPage = Boolean(connection?.pageInfo?.hasNextPage);

    // Empty page or hasNextPage without a cursor would re-fetch the same page and duplicate rows.
    if (nodes.length === 0 || (nextPage && !endCursor)) {
      if (nodes.length > 0) {
        allNodes.push(...nodes);
      }
      hasNextPage = false;
      break;
    }

    allNodes.push(...nodes);
    hasNextPage = nextPage;
    after = endCursor || undefined;
    pageCount += 1;
  }

  return { nodes: allNodes, truncated: hasNextPage };
};
